import React, { useCallback, useEffect, useState } from 'react';
import {
  AppBar,
  Badge,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Category,
  MedicalServices,
  People,
  LocalHospital,
  Settings,
  Logout,
  AccountCircle,
  Payment,
  AccountBalance,
  Assessment,
  TrendingUp,
  CreditCard,
  Assignment,
  Visibility,
  BarChart,
  ExpandLess,
  ExpandMore,
  Business,
  Subscriptions,
  AttachMoney,
  Security,
  LocationCity,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openGroups, setOpenGroups] = useState<{ [key: string]: boolean }>({
    services: true,
    subscriptions: true,
    payments: true,
    adminConsole: true,
  });
  const [notificationSummary, setNotificationSummary] = useState({
    unread: 0,
    total: 0,
    lastSeen: null as string | null,
  });
  const { user, logout, hasAnyPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
    navigate('/login');
  };

  const handleGroupToggle = (group: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const menuGroups = {
    overview: {
      title: 'Overview',
      items: [
        { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
        {
          text: 'Notifications',
          icon: <Visibility />,
          path: '/notifications',
          requiredPermissions: ['admin.activity_logs.view', 'admin.admin_comments.manage'],
        },
      ],
    },
    services: {
      title: 'Service Management',
      items: [
        {
          text: 'Service Categories',
          icon: <Category />,
          path: '/categories',
          requiredPermissions: ['admin.service_categories.manage'],
        },
        {
          text: 'Professional Services',
          icon: <MedicalServices />,
          path: '/services',
          requiredPermissions: ['admin.verification.manage'],
        },
        { text: 'Professionals', icon: <People />, path: '/professionals', requiredPermissions: ['admin.users.view'] },
        { text: 'Patients', icon: <LocalHospital />, path: '/patients', requiredPermissions: ['admin.users.view'] },
      ],
    },
    subscriptions: {
      title: 'Subscriptions & Leads',
      items: [
        {
          text: 'Subscription Plans',
          icon: <CreditCard />,
          path: '/subscription-plans',
          requiredPermissions: ['admin.subscriptions.view', 'admin.subscriptions.manage'],
        },
        { text: 'Lead Monitoring', icon: <Assignment />, path: '/leads', requiredPermissions: ['admin.leads.monitor'] },
        {
          text: 'Lead Statistics',
          icon: <BarChart />,
          path: '/leads/statistics',
          requiredPermissions: ['admin.leads.analytics.view'],
        },
        {
          text: 'Contact Reveal Events',
          icon: <Visibility />,
          path: '/contact-reveal-events',
          requiredPermissions: ['admin.contact_reveal.view'],
        },
      ],
    },
    payments: {
      title: 'Payments & Finance',
      items: [
        { text: 'Payment Dashboard', icon: <Payment />, path: '/payments', requiredPermissions: ['admin.payments.view'] },
        {
          text: 'Transactions',
          icon: <AccountBalance />,
          path: '/payments/transactions',
          requiredPermissions: ['admin.payments.view'],
        },
        // { text: 'Payout Approval', icon: <TrendingUp />, path: '/payments/payouts' },
        // { text: 'Commissions', icon: <Assessment />, path: '/payments/commissions' },
        {
          text: 'Financial Reports',
          icon: <Assessment />,
          path: '/payments/reports',
          requiredPermissions: ['admin.payments.view'],
        },
      ],
    },
    adminConsole: {
      title: 'Administration',
      items: [
        {
          text: 'Role Management',
          icon: <Security />,
          path: '/admin/roles',
          requiredPermissions: ['admin.role_management.view', 'admin.user_roles.assign'],
        },
        {
          text: 'User Management',
          icon: <People />,
          path: '/admin/users',
          requiredPermissions: ['admin.users.view'],
        },
      ],
    },
    settings: {
      title: 'Settings',
      items: [
        {
          text: 'Settings',
          icon: <Settings />,
          path: '/settings',
          requiredPermissions: ['admin.system_settings.manage', 'admin.payment.settings.manage'],
        },
        {
          text: 'City Management',
          icon: <LocationCity />,
          path: '/admin/cities',
          requiredPermissions: ['admin.system_settings.manage'],
        },
      ],
    },
  };

  const canAccessMenuItem = (requiredPermissions?: string[]): boolean => {
    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true;
    }
    return hasAnyPermission(requiredPermissions);
  };

  const loadNotificationSummary = useCallback(async () => {
    try {
      const response = await apiService.getAdminActivityLogs({
        page: 1,
        per_page: 1,
        admin_only: true,
      });

      setNotificationSummary({
        unread: response.unread_count,
        total: response.pagination.total,
        lastSeen: response.last_seen || null,
      });
    } catch (error) {
      console.error('Failed to load notification summary:', error);
    }
  }, []);

  useEffect(() => {
    loadNotificationSummary();
    const interval = setInterval(loadNotificationSummary, 30000);
    return () => clearInterval(interval);
  }, [loadNotificationSummary]);

  useEffect(() => {
    const handleMarkedRead = () => {
      loadNotificationSummary();
    };
    window.addEventListener('adminNotificationsMarkedRead', handleMarkedRead);
    return () => window.removeEventListener('adminNotificationsMarkedRead', handleMarkedRead);
  }, [loadNotificationSummary]);

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Arogya Admin
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {/* Overview */}
        {menuGroups.overview.items.filter((item) => canAccessMenuItem(item.requiredPermissions)).map((item) => {
          const isNotificationItem = item.text === 'Notifications';
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
              >
                <ListItemIcon>
                  {isNotificationItem ? (
      <Badge
                      badgeContent={notificationSummary.unread}
                      color="error"
                      invisible={notificationSummary.unread === 0}
                    >
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          );
        })}
        
        <Divider sx={{ my: 1 }} />
        
        {/* Service Management */}
        {(() => {
          const visibleItems = menuGroups.services.items.filter((item) => canAccessMenuItem(item.requiredPermissions));
          if (visibleItems.length === 0) {
            return null;
          }
          return (
            <>
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleGroupToggle('services')}>
            <ListItemIcon>
              <Business />
            </ListItemIcon>
            <ListItemText primary={menuGroups.services.title} />
            {openGroups.services ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={openGroups.services} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
                  {visibleItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                  sx={{ pl: 4 }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
            </>
          );
        })()}
        
        <Divider sx={{ my: 1 }} />
        
        {/* Subscriptions & Leads */}
        {(() => {
          const visibleItems = menuGroups.subscriptions.items.filter((item) => canAccessMenuItem(item.requiredPermissions));
          if (visibleItems.length === 0) {
            return null;
          }
          return (
            <>
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleGroupToggle('subscriptions')}>
            <ListItemIcon>
              <Subscriptions />
            </ListItemIcon>
            <ListItemText primary={menuGroups.subscriptions.title} />
            {openGroups.subscriptions ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={openGroups.subscriptions} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
                  {visibleItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                  sx={{ pl: 4 }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
            </>
          );
        })()}
        
        <Divider sx={{ my: 1 }} />
        
        {/* Payments & Finance */}
        {(() => {
          const visibleItems = menuGroups.payments.items.filter((item) => canAccessMenuItem(item.requiredPermissions));
          if (visibleItems.length === 0) {
            return null;
          }
          return (
            <>
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleGroupToggle('payments')}>
            <ListItemIcon>
              <AttachMoney />
            </ListItemIcon>
            <ListItemText primary={menuGroups.payments.title} />
            {openGroups.payments ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={openGroups.payments} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
                  {visibleItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                  sx={{ pl: 4 }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
            </>
          );
        })()}
        
        <Divider sx={{ my: 1 }} />
        
        {/* Administration */}
        {(() => {
          const visibleItems = menuGroups.adminConsole.items.filter((item) => canAccessMenuItem(item.requiredPermissions));
          if (visibleItems.length === 0) {
            return null;
          }
          return (
            <>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleGroupToggle('adminConsole')}>
                  <ListItemIcon>
                    <Security />
                  </ListItemIcon>
                  <ListItemText primary={menuGroups.adminConsole.title} />
                  {openGroups.adminConsole ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={openGroups.adminConsole} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {visibleItems.map((item) => (
                    <ListItem key={item.text} disablePadding>
                      <ListItemButton
                        selected={location.pathname === item.path}
                        onClick={() => navigate(item.path)}
                        sx={{ pl: 4 }}
                      >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </>
          );
        })()}
        
        <Divider sx={{ my: 1 }} />
        
        {/* Settings */}
        {menuGroups.settings.items.filter((item) => canAccessMenuItem(item.requiredPermissions)).map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Healthcare Management System
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">
              Welcome, {user?.name}
            </Typography>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                <AccountCircle />
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
            >
              <MenuItem onClick={handleProfileMenuClose}>
                <ListItemIcon>
                  <AccountCircle fontSize="small" />
                </ListItemIcon>
                Profile
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
